import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraEmptyState, SpecraStatusDot } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { EntityTable } from '../_shared/components/EntityTable';
import { formatMoney } from '../_shared/lib/recordLabels';
import { fmtPct } from './format';
import { countsOnTimePercent } from './hooks';
// A compact compliance dot keeps the four KPI columns scannable.
function kpi(ok) {
    return /*#__PURE__*/ _jsx(SpecraStatusDot, {
        label: ok ? 'Compliant' : 'Not compliant',
        tone: ok ? 'success' : 'neutral'
    });
}
export function ProgramHealthTable({ rows, isLoading, isError, onRetry, deliverables, openCorrectionCountsByProgram, openCorrectionsLoading, openCorrectionsError }) {
    const navigate = useNavigate();
    // The on-time and late columns read the contract model (ListOccurrences'
    // on_time and state_counts for the program's current contract period), never
    // ProgramHealthRow.deliverableOnTimePct, which is the old model's KPI CTE.
    // Each opens the program's own Deliverables list behind the figure.
    const columns = useMemo(()=>[
            {
                key: 'programName',
                header: 'Program',
                render: (r)=>r.programName || r.contractNumber || 'Unnamed program'
            },
            {
                key: 'contractNumber',
                header: 'Contract #'
            },
            {
                key: 'portfolioName',
                header: 'Portfolio',
                render: (r)=>r.portfolioName || r.sectorName || '—'
            },
            {
                key: 'currentTcv',
                header: 'TCV',
                align: 'right',
                // A table cell, so cents (formatMoney); the rollup KPIs above stay whole dollars.
                render: (r)=>r.currentTcv == null ? '—' : formatMoney(r.currentTcv)
            },
            {
                key: 'fundedPercent',
                header: 'Funded',
                align: 'right',
                render: (r)=>fmtPct(r.fundedPercent)
            },
            {
                key: 'deliverableOnTimePct',
                header: 'On-time',
                align: 'right',
                render: (r)=>/*#__PURE__*/ _jsx(OnTimeCell, {
                        program: r,
                        counts: deliverables.byProgram.get(r.programId),
                        isError: deliverables.failedIds.has(r.programId),
                        onOpen: ()=>navigate(`/delivery/programs/${r.programId}/deliverables?state=approved`)
                    })
            },
            {
                key: 'lateDeliverables',
                header: 'Late',
                align: 'right',
                render: (r)=>/*#__PURE__*/ _jsx(LateCell, {
                        program: r,
                        counts: deliverables.byProgram.get(r.programId),
                        isError: deliverables.failedIds.has(r.programId),
                        onOpen: ()=>navigate(`/delivery/programs/${r.programId}/deliverables?state=late`)
                    })
            },
            {
                key: 'kpi1',
                header: 'K1',
                align: 'center',
                render: (r)=>kpi(r.kpi1Compliant)
            },
            {
                key: 'kpi2',
                header: 'K2',
                align: 'center',
                render: (r)=>kpi(r.kpi2Compliant)
            },
            {
                key: 'kpi3',
                header: 'K3',
                align: 'center',
                render: (r)=>kpi(r.kpi3Compliant)
            },
            {
                key: 'kpi4',
                header: 'K4',
                align: 'center',
                render: (r)=>kpi(r.kpi4Compliant)
            },
            {
                key: 'selfAssessment',
                header: 'Self-assessment',
                render: (r)=>/*#__PURE__*/ _jsx(SelfAssessmentCell, {
                        program: r,
                        isLoading: isLoading,
                        isError: isError,
                        onOpen: ()=>navigate(`/delivery/programs/${r.programId}/audits`)
                    })
            },
            {
                key: 'openCorrections',
                header: 'Open Corrections',
                align: 'right',
                render: (r)=>/*#__PURE__*/ _jsx(OpenCorrectionsCell, {
                        program: r,
                        count: openCorrectionCountsByProgram.get(r.programId) ?? 0,
                        isLoading: openCorrectionsLoading,
                        isError: openCorrectionsError,
                        onOpen: ()=>navigate(`/delivery/programs/${r.programId}/audits`)
                    })
            }
        ], [
        deliverables,
        isLoading,
        isError,
        openCorrectionCountsByProgram,
        openCorrectionsLoading,
        openCorrectionsError,
        navigate
    ]);
    return /*#__PURE__*/ _jsx(EntityTable, {
        data: rows,
        columns: columns,
        testid: Testids.commandCenter.programTable,
        rowTestid: Testids.commandCenter.programRow,
        rowKey: (r)=>r.programId,
        isLoading: isLoading,
        error: isError ? new Error('Failed to load programs') : undefined,
        onRetry: onRetry,
        // Without an emptyState, EntityTable renders bare column headers on a
        // no-programs result; match the rollup sections' friendly empty copy.
        emptyState: /*#__PURE__*/ _jsx(SpecraEmptyState, {
            framed: true,
            size: "compact",
            title: "No programs in scope."
        }),
        onRowClick: (r)=>navigate(`/delivery/programs/${r.programId}`)
    });
}
function OnTimeCell({ program, counts, isError, onOpen }) {
    // A pending or failed read never reads as a rate, and a program with no
    // timed approval yet says so: 0% would read as terrible delivery.
    const unavailable = isError || !counts;
    const pct = counts ? countsOnTimePercent(counts) : null;
    return /*#__PURE__*/ _jsx(SpecraButton, {
        disabled: unavailable,
        onClick: (e)=>{
            // The row click navigates to the program page; that behaviour stays.
            e.stopPropagation();
            onOpen();
        },
        title: unavailable ? 'The deliverable counts are unavailable' : "Approved on time over approved with a known date, this contract period. Open the program's approved deliverables.",
        "aria-label": `On-time deliveries for ${program.programName || program.contractNumber}`,
        "data-testid": Testids.commandCenter.onTimeCell,
        size: "inline",
        variant: "link",
        children: isError ? /*#__PURE__*/ _jsx("span", {
            className: "text-muted-foreground",
            children: "Unavailable"
        }) : !counts ? /*#__PURE__*/ _jsx("span", {
            className: "text-muted-foreground",
            children: "Loading…"
        }) : pct === null ? /*#__PURE__*/ _jsx("span", {
            className: "text-muted-foreground",
            children: "No approvals yet"
        }) : /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                counts.onTime,
                " of ",
                counts.timingKnown,
                ' ',
                /*#__PURE__*/ _jsxs("span", {
                    className: "text-muted-foreground",
                    children: [
                        "(",
                        pct,
                        "%)"
                    ]
                })
            ]
        })
    });
}
function LateCell({ program, counts, isError, onOpen }) {
    const unavailable = isError || !counts;
    const late = counts?.stateCounts.late ?? 0;
    return /*#__PURE__*/ _jsx(SpecraButton, {
        disabled: unavailable,
        onClick: (e)=>{
            e.stopPropagation();
            onOpen();
        },
        title: unavailable ? 'The deliverable counts are unavailable' : 'Open the late deliverables',
        "aria-label": `Late deliverables for ${program.programName || program.contractNumber}`,
        "data-testid": Testids.cmPortfolio.lateCell,
        size: "inline",
        variant: "link",
        children: isError ? /*#__PURE__*/ _jsx("span", {
            className: "text-muted-foreground",
            children: "Unavailable"
        }) : !counts ? /*#__PURE__*/ _jsx("span", {
            className: "text-muted-foreground",
            children: "Loading…"
        }) : /*#__PURE__*/ _jsx("span", {
            className: late > 0 ? 'text-danger' : undefined,
            children: late
        })
    });
}
// SAD-178 (RPT-02): the program's current self-assessment (Program Lead audit
// state), read straight off ProgramHealthRow — no client-side aggregation, so
// this can never disagree with pangaea's cur_aud selection. A program with no
// current self-assessment reads "None", never a 0/0 that would misread as a
// failing score. A pending/failed dashboard read must render as unavailable
// (the SAME read the rest of the row's KPI columns depend on), not as "None".
function SelfAssessmentCell({ program, isLoading, isError, onOpen }) {
    const unavailable = isLoading || isError;
    return /*#__PURE__*/ _jsx(SpecraButton, {
        disabled: unavailable,
        onClick: (e)=>{
            // The row click navigates to the program page; that behaviour stays.
            e.stopPropagation();
            onOpen();
        },
        title: unavailable ? 'The self-assessment is unavailable' : 'Open the Audits surface',
        "aria-label": `Self-assessment for ${program.programName || program.programId}`,
        "data-testid": Testids.commandCenter.selfAssessmentCell,
        size: "inline",
        variant: "link",
        children: isError ? /*#__PURE__*/ _jsx("span", {
            className: "text-muted-foreground",
            children: "— unavailable"
        }) : isLoading ? /*#__PURE__*/ _jsx("span", {
            className: "text-muted-foreground",
            children: "—"
        }) : !program.hasSelfAssessment ? /*#__PURE__*/ _jsx("span", {
            className: "text-muted-foreground",
            children: "None"
        }) : /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                program.selfAssessmentState,
                program.selfAssessmentPeriodMonth && /*#__PURE__*/ _jsxs("span", {
                    className: "text-muted-foreground",
                    children: [
                        " (",
                        program.selfAssessmentPeriodMonth,
                        ")"
                    ]
                }),
                program.selfAssessmentReadinessDen > 0 && /*#__PURE__*/ _jsxs("span", {
                    className: "tabular-nums text-muted-foreground",
                    children: [
                        ' ',
                        program.selfAssessmentReadinessNum,
                        "/",
                        program.selfAssessmentReadinessDen
                    ]
                })
            ]
        })
    });
}
// SAD-178: the open-Corrections count, sourced from GetOpenCorrectiveActionCounts
// (grouped by program in CommandCenterPage) — not a second computation. A
// missing programId in that map means zero per the RPC's own contract, but a
// pending/failed read of the RPC itself must render as unavailable, never 0 —
// an empty program is not the same thing as "we don't know".
function OpenCorrectionsCell({ program, count, isLoading, isError, onOpen }) {
    const unavailable = isLoading || isError;
    return /*#__PURE__*/ _jsx(SpecraButton, {
        disabled: unavailable,
        onClick: (e)=>{
            e.stopPropagation();
            onOpen();
        },
        title: unavailable ? 'The open-Corrections count is unavailable' : 'Open the Audits surface',
        "aria-label": `Open Corrections for ${program.programName || program.programId}`,
        "data-testid": Testids.commandCenter.openCorrectionsCell,
        size: "inline",
        variant: "link",
        children: isError ? /*#__PURE__*/ _jsx("span", {
            className: "text-muted-foreground",
            children: "— unavailable"
        }) : isLoading ? /*#__PURE__*/ _jsx("span", {
            className: "text-muted-foreground",
            children: "—"
        }) : count
    });
}
